import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import type { DraftStatus } from '../../api/client'
import { describeError } from '../../lib/describeError'
import { sourceHref } from '../../lib/reviewSources'
import { usePublicBaseUrl } from '../../lib/publicBaseUrls'
import { SocialBlock } from '../../pages/SocialBlock'
import { recheck, type ChecklistItem, type PieceDetail } from '../api'
import { useEditorialTitle } from '../titleContext'
import { pieceHref } from '../links'
import {
  blockingCount, claimFate, SAVE_FIRST, claimSourceLabel, etStamp, FATE_TEXT, historyEntries, isFactChecking, isOriginalSource, offersAgentActions, RECHECK_NOTE, sourceKindLabel,
  type ClaimFate,
} from './pieceHelpers'
import { counterText, DESCRIPTION_LIMIT, searchCrumb, searchPreview, TITLE_LIMIT } from './searchPreview'
import { ReadersPanel } from './ReadersPanel'
import { VersionsTab } from './VersionsTab'
import { SuggestionsTab } from './SuggestionsTab'
import { openSuggestions } from './suggestions'
import type { FigureAttrs } from './figure'
import type { LocalFields } from './usePieceAutosave'
import { BrandPicker } from './BrandPicker'
import { btn, btnSmall, errorText, field, focus } from './ui'

export type SideTab = 'checklist' | 'facts' | 'sources' | 'search' | 'social' | 'history' | 'versions' | 'readers' | 'suggestions'
const TABS: { key: SideTab; label: string }[] = [
  { key: 'checklist', label: 'Checklist' }, { key: 'facts', label: 'Facts' }, { key: 'sources', label: 'Sources' },
  { key: 'search', label: 'Search' }, { key: 'social', label: 'Social' }, { key: 'history', label: 'History' },
  { key: 'versions', label: 'Versions' },
]
const SUGGESTIONS_TAB = { key: 'suggestions' as const, label: 'Suggestions' }
const READERS_TAB = { key: 'readers' as const, label: 'Readers' }

const ICON = 'h-[22px] w-[22px] flex-none rounded-full text-center font-ui text-[13px] font-bold leading-[22px]'
const ICON_OK = `${ICON} bg-radar-green/15 text-radar-green`
const ICON_WARN = `${ICON} bg-amber-glow/20 text-amber-deep`
const ICON_BAD = `${ICON} bg-radar-red/15 text-radar-red`
const ICON_PLAIN = `${ICON} bg-ink-600 text-paper-dim`
const ROW = 'flex items-start gap-3 border-t border-ink-500 py-3'
/** The search fields the Search tab saves. */
export type SearchField = 'seo_title' | 'meta_description' | 'slug'

const H2 = 'm-0 mb-1.5 font-ui text-[15px] font-semibold text-paper'

type Props = {
  detail: PieceDetail
  sectionLabel: string
  publicationSlug: string
  /** Show the sentence in the article (Show me). */
  onShowClaim: (text: string) => void
  /** Search fields and the credit are open for edits. */
  editable: boolean
  /** A fact check runs on this piece: the fields wait, read-only, until it ends. */
  locked?: boolean
  local: LocalFields
  onField: (f: SearchField, v: string) => void
  status: DraftStatus | undefined
  onRecheckStarted: () => void
  /** Saves what waits first; false refuses the write. */
  beforeWrite: () => Promise<boolean>
  onChanged: () => void
  /** Ask for a tab (n changes on each ask, so the same tab can be asked for twice). */
  openTab?: { tab: SideTab; n: number }
  /** Put a photograph into the article body. */
  onInsertFigure: (a: FigureAttrs) => void
  /** The section's URL path, for links between versions. */
  path: string
  /** A copy of the piece a write here returned, for the page to adopt (and put in the editor). */
  onDetail: (r: PieceDetail) => void
  /** An edit draft: the live article's address can't change. */
  slugFixed?: boolean
  /** Pit Lane brand pages are on for the title: show the brand picker. */
  brandsOn?: boolean
  /** Save the piece's brand (null clears it). */
  onBrand?: (brandId: number | null) => Promise<void>
}

export function SidePanel(props: Props) {
  const imported = props.detail.origin === 'import'
  const [tab, setTab] = useState<SideTab>(imported && openSuggestions(props.detail).length > 0 ? 'suggestions' : 'checklist')
  const [seen, setSeen] = useState(props.openTab)
  if (props.openTab !== seen) { setSeen(props.openTab); if (props.openTab) setTab(props.openTab.tab) }
  const base = props.detail.status === 'published' || props.detail.status === 'unpublished' ? [...TABS, READERS_TAB] : TABS
  const tabs = imported ? [SUGGESTIONS_TAB, ...base] : base
  const suggestionCount = openSuggestions(props.detail).length
  const shown = tabs.some((t) => t.key === tab) ? tab : 'checklist'
  const count = blockingCount(props.detail.checklist ?? [])
  return (
    <aside aria-label="About this piece" className="min-w-0 flex-[1_1_340px] self-start overflow-hidden rounded-xl border border-ink-500 bg-ink-700 font-ui text-[14px] text-paper lg:sticky lg:top-[84px] lg:max-w-[420px]">
      {props.brandsOn && props.onBrand && (
        <div className="border-b border-ink-500 px-[18px] pb-4 pt-4">
          <BrandPicker publicationSlug={props.publicationSlug} brand={props.detail.brand ?? null}
                       editable={props.editable && !props.locked} onPick={props.onBrand} />
        </div>
      )}
      <div role="tablist" aria-label="Piece details" className="flex flex-wrap gap-0.5 border-b border-ink-500 px-2 pt-2">
        {tabs.map((t) => (
          <button key={t.key} type="button" role="tab" id={`piece-tab-${t.key}`} aria-selected={shown === t.key} aria-controls="piece-tabpanel"
                  onClick={() => setTab(t.key)}
                  className={`inline-flex min-h-10 items-center gap-1.5 border-b-2 px-2.5 text-[13px] font-semibold ${focus} ${
                    shown === t.key ? 'border-amber-glow text-paper' : 'border-transparent text-paper-faint hover:text-paper'}`}>
            {t.label}
            {t.key === 'suggestions' && suggestionCount > 0 && (
              <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-glow px-1.5 text-[11px] text-ink-900">
                {suggestionCount}<span className="sr-only"> open</span>
              </span>
            )}
            {t.key === 'checklist' && count > 0 && (
              <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-glow px-1.5 text-[11px] text-ink-900">
                {count}<span className="sr-only"> to fix</span>
              </span>
            )}
          </button>
        ))}
      </div>
      <div role="tabpanel" id="piece-tabpanel" aria-labelledby={`piece-tab-${shown}`} className="flex flex-col gap-1 px-[18px] pb-5 pt-4">
        {shown === 'suggestions' && (
          <SuggestionsTab detail={props.detail} editable={props.editable && !props.locked} beforeWrite={props.beforeWrite} onDetail={props.onDetail} />
        )}
        {shown === 'checklist' && <ChecklistTab {...props} onFix={() => setTab('search')} />}
        {shown === 'facts' && <FactsTab {...props} />}
        {shown === 'sources' && <SourcesTab {...props} />}
        {shown === 'search' && <SearchTab {...props} />}
        {shown === 'social' && <SocialTab {...props} />}
        {shown === 'history' && <HistoryTab {...props} />}
        {shown === 'versions' && (
          <VersionsTab detail={props.detail} path={props.path} sectionLabel={props.sectionLabel} editable={props.editable && !props.locked}
                       beforeWrite={props.beforeWrite} onChanged={props.onChanged} onInsertFigure={props.onInsertFigure} />
        )}
        {shown === 'readers' && <ReadersPanel pieceId={props.detail.id} sectionLabel={props.sectionLabel} />}
      </div>
    </aside>
  )
}

function ChecklistTab({ detail, onShowClaim, onFix }: Props & { onFix: () => void }) {
  const items = detail.checklist ?? []
  const web = detail.sources.filter((s) => s.kind === 'web').length
  const claims = detail.verification.claims ?? []
  const unchecked = claims.filter((c) => ['not_found', 'unsupported'].includes(claimFate(c, detail.verification))).length
  return (
    <>
      <h2 className={H2}>Before you publish</h2>
      {items.length === 0 && <p className="m-0 text-[13px] text-paper-dim">Nothing left to check.</p>}
      {items.map((item: ChecklistItem) => (
        <div key={item.key} className={ROW}>
          <span className={item.blocking ? ICON_WARN : ICON_PLAIN} aria-hidden="true">{item.blocking ? '!' : 'i'}</span>
          <span className="flex flex-1 flex-col gap-0.5">
            <strong className="font-semibold">{item.title}</strong>
            {item.detail && <span className="text-[13px] text-paper-dim">{item.detail}</span>}
          </span>
          {item.claim && (
            <button type="button" className={btnSmall} onClick={() => onShowClaim(item.claim as string)}>Show me</button>
          )}
          {!item.claim && item.kind === 'search_title' && <button type="button" className={btnSmall} onClick={onFix}>Fix</button>}
        </div>
      ))}
      <p className="m-0 mt-2.5 text-[12px] text-paper-faint">
        Written from {detail.sources.length} {detail.sources.length === 1 ? 'source' : 'sources'}
        {web ? ` (${web} from the web)` : ''} · {unchecked} of {claims.length} claims could not be checked
        {detail.requested_by ? ` · asked for by ${detail.requested_by}` : ''}, {etStamp(detail.created_at)}
      </p>
    </>
  )
}

const FATE_ICON: Record<ClaimFate, [string, string]> = {
  supported: [ICON_OK, '✓'], confirmed: [ICON_OK, '✓'], cut: [ICON_PLAIN, '–'], not_found: [ICON_BAD, '✗'], unsupported: [ICON_WARN, '✗'],
}

function FactsTab({ detail, sectionLabel, status, editable, onRecheckStarted, beforeWrite }: Props) {
  const claims = detail.verification.claims ?? []
  const checking = isFactChecking(status)
  const run = useMutation({
    mutationFn: async () => {
      if (!(await beforeWrite())) throw new Error(SAVE_FIRST)
      return recheck(detail.id)
    },
    onSuccess: onRecheckStarted,
  })
  const canRecheck = editable && detail.status !== 'sources_ready' && offersAgentActions(detail.origin)
  return (
    <>
      <h2 className={H2}>Every claim, checked</h2>
      <p className="m-0 mb-1.5 text-[13px] text-paper-dim">The writer checks each statement against the sources and cuts what it cannot support.</p>
      {canRecheck && (
        <div className="flex flex-wrap items-center gap-2 pb-2">
          <button type="button" className={btn} disabled={checking || run.isPending || !!status?.running} onClick={() => run.mutate()}>
            {checking ? 'Checking… about a minute' : run.isPending ? 'Starting…' : 'Check facts again'}
          </button>
          {!checking && status?.running && <span className="text-[12px] text-paper-faint">A piece is being written for this title.</span>}
        </div>
      )}
      {checking && <p role="status" className="sr-only">Checking the facts again. It takes about a minute.</p>}
      {run.isError && <p className={errorText}>{describeError(run.error)}</p>}
      {claims.length === 0 && <p className="m-0 text-[13px] text-paper-dim">No claims recorded.</p>}
      {claims.map((c, i) => {
        const fate = claimFate(c, detail.verification)
        const [cls, icon] = FATE_ICON[fate]
        const src = fate === 'supported' ? claimSourceLabel(c.source_id, detail.sources, sectionLabel) : null
        const note = fate === 'unsupported' && c.note ? `${FATE_TEXT[fate]}: ${c.note}` : FATE_TEXT[fate]
        return (
          <div key={i} className={ROW}>
            <span className={cls} aria-hidden="true">{icon}</span>
            <span className="flex flex-1 flex-col gap-0.5">
              <span className={`font-serif text-[15px] ${fate === 'cut' ? 'text-paper-faint line-through' : ''}`}>{c.text}</span>
              <span className="text-[12px] text-paper-faint">{src ?? note}</span>
            </span>
          </div>
        )
      })}
    </>
  )
}

function SourcesTab({ detail, sectionLabel, publicationSlug }: Props) {
  const publicBase = usePublicBaseUrl(publicationSlug)
  const n = detail.sources.length
  return (
    <>
      <h2 className={H2}>{n} {n === 1 ? 'source' : 'sources'}</h2>
      {n === 0 && <p className="m-0 text-[13px] text-paper-dim">No sources recorded.</p>}
      {detail.sources.map((s) => {
        const href = sourceHref(publicBase, s)
        let where = s.issue_label ?? ''
        if (!where && s.kind === 'web' && s.url) { try { where = new URL(s.url).host.replace(/^www\./, '') } catch { where = '' } }
        const quoted = s.passages.length ? `quoted ${s.passages.length === 1 ? 'once' : s.passages.length === 2 ? 'twice' : `${s.passages.length} times`}` : ''
        const meta = [where, quoted, s.reference_only ? 'reference only' : ''].filter(Boolean).join(' · ')
        return (
          <div key={s.id} className={ROW}>
            <span className={`inline-flex h-6 flex-none items-center rounded-full px-2.5 text-[12px] font-semibold ${
              s.kind === 'member' ? 'bg-radar-violet/15 text-radar-violet' : s.kind === 'web' ? 'bg-radar-cyan/15 text-radar-cyan' : 'bg-ink-600 text-paper-dim'}`}>
              {sourceKindLabel(s.kind, sectionLabel)}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="flex flex-wrap items-center gap-1.5">
                {href
                  ? <a href={href} target="_blank" rel="noreferrer" className={`font-semibold text-paper hover:underline ${focus}`}>{s.title}</a>
                  : <span className="font-semibold">{s.title}</span>}
                {isOriginalSource(s, detail.made_from) && (
                  <span className="inline-flex h-5 items-center rounded-full bg-amber-wash px-2 text-[11px] font-semibold text-amber-glow"
                        title="This version was made from this piece">Original</span>
                )}
              </span>
              {meta && <span className="text-[12px] text-paper-faint">{meta}</span>}
            </span>
          </div>
        )
      })}
    </>
  )
}

function Counter({ length, limit }: { length: number; limit: number }) {
  const c = counterText(length, limit)
  return <span aria-live="polite" className={`text-[12px] ${c.over ? 'font-semibold text-amber-deep' : 'text-paper-faint'}`}>{c.text}</span>
}

export const SLUG_FIXED = "A live article's address can't change."

function SearchTab({ detail, sectionLabel, editable: canEdit, locked, local, onField, slugFixed }: Props) {
  const editable = canEdit && !locked
  const seo = local.seo_title ?? detail.seo_title ?? ''
  const meta = local.meta_description ?? detail.meta_description ?? ''
  const slug = local.slug ?? detail.slug ?? ''
  const p = searchPreview({ seo_title: seo, title: detail.title, meta_description: meta })
  return (
    <>
      <h2 className={`${H2} mb-2.5`}>How it looks in Google</h2>
      <div className="flex flex-col gap-0.5 rounded-lg border border-ink-500 px-3.5 py-3">
        <span className="text-[12px] text-paper-dim">{searchCrumb(detail.public_url, sectionLabel)}</span>
        <span className="text-[18px] leading-snug text-radar-cyan">{p.title}</span>
        <span className="text-[13px] text-paper-dim">{p.description || 'No search description yet.'}</span>
      </div>
      {!editable && <p className="m-0 mt-2 text-[12px] text-paper-faint">{locked && canEdit ? `${RECHECK_NOTE}.` : "These can't change on this piece."}</p>}
      <label className="mt-3.5 flex flex-col gap-1.5 font-semibold">
        Search title
        <input type="text" className={field} value={seo} disabled={!editable} onChange={(e) => onField('seo_title', e.target.value)} />
      </label>
      <Counter length={seo.length} limit={TITLE_LIMIT} />
      <label className="mt-3 flex flex-col gap-1.5 font-semibold">
        Search description
        <textarea rows={3} className={field} value={meta} disabled={!editable} onChange={(e) => onField('meta_description', e.target.value)} />
      </label>
      <Counter length={meta.length} limit={DESCRIPTION_LIMIT} />
      <label className="mt-3 flex flex-col gap-1.5 font-semibold">
        Web address
        <input type="text" className={field} value={slug} disabled={!editable} readOnly={slugFixed}
               aria-describedby={slugFixed ? 'slug-fixed' : undefined}
               onChange={(e) => { if (!slugFixed) onField('slug', e.target.value) }} />
      </label>
      <span id={slugFixed ? 'slug-fixed' : undefined} className="break-all text-[12px] text-paper-faint">
        {slugFixed ? SLUG_FIXED : detail.public_url ?? 'The last part of the page address. Letters, numbers and hyphens.'}
      </span>
    </>
  )
}

function SocialTab({ detail, onChanged, beforeWrite }: Props) {
  if (detail.status === 'superseded') return <p className="m-0 text-[13px] text-paper-dim">Social posts belong to the newer version.</p>
  return <SocialBlock draftId={detail.id} social={detail.social} orphaned={detail.orphaned_posts} postingOn={detail.social_posting_on} onChange={onChanged} beforeWrite={beforeWrite} />
}

function HistoryTab({ detail }: Props) {
  const { section } = useEditorialTitle()
  return (
    <>
      <h2 className={H2}>History</h2>
      {historyEntries(detail).map((h, i) => (
        <div key={i} className={ROW}>
          <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-amber-glow" aria-hidden="true" />
          <span className="flex flex-1 flex-col gap-0.5">
            <strong className="font-semibold">{h.what}</strong>
            {h.detail && <span className="text-[13px] text-paper-dim">{h.detail}</span>}
            {h.link && <Link to={pieceHref(section.path, h.link.pieceId)} className={`text-[13px] text-amber-deep underline ${focus}`}>{h.link.label}</Link>}
            {h.when && <span className="text-[12px] text-paper-faint">{h.when}</span>}
          </span>
        </div>
      ))}
    </>
  )
}
